import Menu from './menu';

import { Modules } from '@kaetram/common/network';

import type Game from '../game';

const SKILL_MAP: { [key: string]: Modules.Skills } = {
        accuracy: Modules.Skills.Accuracy,
        strength: Modules.Skills.Strength,
        defense: Modules.Skills.Defense,
        archery: Modules.Skills.Archery,
        magic: Modules.Skills.Magic
    },
    PORTRAIT_SIZE = 48,
    // Must match the server cap (Modules.Constants.MAX_STAT_ALLOCATION). If the
    // client cap is higher, the UI lets you allocate points the server silently
    // rejects, and they vanish on relogin.
    MAX_ALLOC_PER_SKILL = Modules.Constants.MAX_STAT_ALLOCATION;

export default class HUD extends Menu {
    private canvas: HTMLCanvasElement = document.querySelector('#hud-portrait-canvas')!;
    private ctx: CanvasRenderingContext2D = this.canvas.getContext('2d')!;
    private nameEl: HTMLElement = document.querySelector('#hud-name')!;
    private levelBadge: HTMLElement = document.querySelector('#hud-level-badge')!;
    private xpBarFill: HTMLElement = document.querySelector('#xp-bar-fill')!;
    private xpBarText: HTMLElement = document.querySelector('#xp-bar-text')!;
    private xpBarContainer: HTMLElement = document.querySelector('#xp-bar-container')!;
    private statPointsBadge: HTMLElement = document.querySelector('#hud-stat-points-badge')!;
    private statPointsBadgeCount: HTMLElement = document.querySelector(
        '#hud-stat-points-count-badge'
    )!;

    private statPanel: HTMLElement = document.querySelector('#stat-panel')!;
    private statPointsLabel: HTMLElement = document.querySelector('#stat-points-count')!;
    private statRows: HTMLLIElement[] = [...document.querySelectorAll('.stat-row')];

    private statPoints = 0;
    private skillAllocations: Partial<{ [key: Modules.Skills]: number }> = {};
    private serverAllocationsLoaded = false;

    // True once we've completed the first effective sync (player.name loaded).
    // Guards against firing a fake "¡Subiste de nivel!" popup on login while
    // the server-side level is being propagated to the client.
    private hasSyncedInitially = false;

    // Whether we've already shown the "puntos pendientes" notice this session.
    // We only show it once, and only when statPoints > 0 at login.
    private pendingPointsShown = false;

    private boostCallback?: (type: Modules.Skills) => void;

    public constructor(private game: Game) {
        super('#player-hud', '#close-stat-panel');

        let portrait = document.querySelector('#hud-portrait')!;
        portrait.addEventListener('click', () => this.togglePanel());

        let helpBtn = document.querySelector('#hud-help-btn')!,
            overlay = document.querySelector('#controls-overlay')!,
            closeBtn = document.querySelector('#controls-close')!;
        helpBtn.addEventListener('click', () => {
            overlay.hidden = false;
        });
        closeBtn.addEventListener('click', () => {
            overlay.hidden = true;
        });
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) overlay.hidden = true;
        });
    }

    public override synchronize(): void {
        let { player } = this.game;
        if (!player.name) return;

        this.container.style.opacity = '1';

        this.nameEl.textContent = player.name;
        this.levelBadge.textContent = `${player.level}`;

        this.drawPortrait();
        this.updateXpBar();

        // On the first effective sync at login we seed local state and, if the
        // player has unassigned stat points, softly remind them. We deliberately
        // do NOT try to detect a "level up" here: at login the level arrives in
        // two steps (spawn data + Experience.Sync), which looked like a jump and
        // fired a bogus "¡Subiste de nivel!" every time. The real level-up
        // notification is pushed by the server (popup in addKillExperience),
        // only on an actual level-up.
        if (!this.hasSyncedInitially) {
            if (!this.serverAllocationsLoaded) this.loadSkillAllocations();
            this.hasSyncedInitially = true;
            this.showPendingPointsIfAny();
        }

        this.refreshPanel();
    }

    /**
     * Shows the "puntos pendientes" toast if the player logged in with
     * statPoints > 0. Idempotent — only fires once per session, and only
     * after both the initial sync AND the statPoints value have arrived
     * (either can arrive first; whichever calls this second wins).
     */
    private showPendingPointsIfAny(): void {
        if (this.pendingPointsShown) return;
        if (!this.hasSyncedInitially) return;
        if (this.statPoints <= 0) return;

        this.pendingPointsShown = true;
        this.game.menu
            .getNotification()
            .show(
                'Puntos pendientes',
                `Tienes ${this.statPoints} punto${
                    this.statPoints === 1 ? '' : 's'
                } de estadística sin asignar. Abre tu perfil para asignarlos.`,
                '#f0c040'
            );
    }

    private drawPortrait(): void {
        let { player, sprites } = this.game,
            spriteName = player.getSpriteName();
        if (!spriteName) return;

        let sprite = sprites.get(spriteName);
        if (!sprite) return;

        if (!sprite.loaded) {
            sprite.load();
            sprite.onLoad(() => this.drawPortrait());
            return;
        }

        let anim = sprite.animations.idle_down;
        if (!anim) return;

        this.ctx.clearRect(0, 0, PORTRAIT_SIZE, PORTRAIT_SIZE);
        this.ctx.imageSmoothingEnabled = false;

        this.ctx.drawImage(
            sprite.image as HTMLImageElement,
            0,
            anim.row * sprite.height,
            sprite.width,
            sprite.height,
            0,
            0,
            PORTRAIT_SIZE,
            PORTRAIT_SIZE
        );
    }

    /**
     * Renderiza la barra de XP del PLAYER (no de skills individuales). Muestra
     * progreso al siguiente nivel usando player.experience,
     * player.prevExperience y player.nextExperience — todos sincronizados
     * por el server vía Experience.Sync al matar mobs / completar quests.
     */

    private updateXpBar(): void {
        let { player } = this.game;

        this.xpBarContainer.hidden = false;

        let current = player.experience ?? 0,
            prev = player.prevExperience ?? 0,
            next = player.nextExperience ?? 0,
            span = Math.max(1, next - prev),
            gained = Math.max(0, current - prev),
            pct = Math.max(0, Math.min(100, (gained / span) * 100));

        // Si aún no llegó info del server (next=0 o max level), la barra queda
        // al 100% en max level, o vacía si aún no sincronizó.
        if (next <= 0) pct = current > 0 ? 100 : 0;

        let pctDisplay = pct < 10 ? pct.toFixed(1) : Math.round(pct),
            prevWidth = parseFloat(this.xpBarFill.style.width) || 0;

        this.xpBarFill.style.width = `${pct}%`;
        this.xpBarText.textContent = `Nv ${player.level} · ${pctDisplay}%`;

        this.xpBarContainer.title =
            next > 0
                ? `${gained.toLocaleString('es-PE')} / ${span.toLocaleString(
                      'es-PE'
                  )} XP hacia el nivel ${player.level + 1}`
                : `Nivel máximo alcanzado`;

        if (pct > prevWidth && this.hasSyncedInitially) {
            this.xpBarFill.classList.remove('xp-flash');
            void this.xpBarFill.offsetWidth;
            this.xpBarFill.classList.add('xp-flash');
        }
    }

    private refreshPanel(): void {
        let { player } = this.game;

        this.statPointsLabel.textContent = `${this.statPoints}`;

        if (this.statPoints > 0) {
            this.statPointsBadgeCount.textContent = `${this.statPoints}`;
            this.statPointsBadge.hidden = false;
        } else this.statPointsBadge.hidden = true;

        for (let row of this.statRows) {
            let skillKey = row.dataset.skill ?? '',
                skill = player.skills[SKILL_MAP[skillKey]],
                levelEl = row.querySelector('.stat-level')!,
                plusBtn = row.querySelector('.stat-plus')!,
                skillId = SKILL_MAP[skillKey],
                allocated = this.skillAllocations[skillId] ?? 0,
                baseLevel = skill ? skill.level : 1;
            if (levelEl) levelEl.textContent = `${baseLevel + allocated}`;

            if (plusBtn) {
                let fresh = plusBtn.cloneNode(true) as HTMLButtonElement;
                plusBtn.replaceWith(fresh);
                fresh.disabled = this.statPoints <= 0 || allocated >= MAX_ALLOC_PER_SKILL;
                fresh.title =
                    allocated >= MAX_ALLOC_PER_SKILL
                        ? `Máximo ${MAX_ALLOC_PER_SKILL} puntos por estadística`
                        : '';
                fresh.addEventListener('click', () => this.allocate(skillKey));
            }
        }
    }

    private allocate(skillKey: string): void {
        if (this.statPoints <= 0) return;

        let skillId = SKILL_MAP[skillKey];

        // Non-optimistic on purpose: don't mutate local state here. We send the
        // boost and let the server validate + persist it, then echo back an
        // authoritative Skill.Batch (connection.ts -> setStatPoints +
        // loadServerAllocations) which refreshes the panel. Mutating locally was
        // the source of the desync where the UI showed points the server had
        // actually rejected — which then "reset" on the next login.
        this.boostCallback?.(skillId);
    }

    public override hide(): void {
        this.statPanel.hidden = true;
    }

    private togglePanel(): void {
        this.statPanel.hidden = !this.statPanel.hidden;
        if (!this.statPanel.hidden) this.refreshPanel();
    }

    public onBoost(callback: (type: Modules.Skills) => void): void {
        this.boostCallback = callback;
    }

    public setStatPoints(points: number): void {
        this.statPoints = points;
        this.refreshPanel();

        // If statPoints arrived after the initial sync, this is the point
        // where we can decide whether to notify. If sync hasn't happened yet,
        // synchronize() will call this method itself once it flips
        // hasSyncedInitially to true — whichever fires second wins.
        this.showPendingPointsIfAny();
    }

    public loadServerAllocations(allocations: { [key: string]: number }): void {
        this.skillAllocations = {};
        for (let [k, v] of Object.entries(allocations))
            this.skillAllocations[Number(k) as Modules.Skills] = v;
        this.serverAllocationsLoaded = true;
        this.saveSkillAllocations();
    }

    private saveSkillAllocations(): void {
        let { player } = this.game;
        if (!player.instance) return;
        localStorage.setItem(
            `kaetram_sa_${player.instance}`,
            JSON.stringify(this.skillAllocations)
        );
    }

    private loadSkillAllocations(): void {
        let { player } = this.game;
        if (!player.instance) return;
        let saved = localStorage.getItem(`kaetram_sa_${player.instance}`);
        if (saved)
            try {
                let parsed = JSON.parse(saved) as { [key: string]: number };
                for (let [k, v] of Object.entries(parsed))
                    this.skillAllocations[Number(k) as Modules.Skills] = v;
            } catch {
                //
            }
    }
}
